import { Calendar, CheckCircle, Download, Eye, FileCheck, Share2, X } from "lucide-react"
import { useEffect, useState } from "react"
import Swal from 'sweetalert2'

import { useNavigate } from "react-router-dom"
import Logo from "../../assets/images/logo/hazer-logo.png"
import apiUrls from "../../config/api-urls"
import Loader from "../../components/common/loader"
import { Button } from "../../components/candidate-dashboard/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../../components/candidate-dashboard/ui/card"
import { Progress } from "../../components/candidate-dashboard/ui/progress"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/candidate-dashboard/ui/table"

export default function ResultTable() {
  const [resultData, setResultData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedResult, setSelectedResult] = useState(null)
  const [showTokenInput, setShowTokenInput] = useState(false)
  const [tempToken, setTempToken] = useState("")
  const [isReapplying, setIsReapplying] = useState(false)
  const navigate = useNavigate();
  

  const fetchTestResults = async (token) => {
    try {
      setIsLoading(true)
      setError(null)
      const authToken = token || localStorage.getItem("authToken")
      if (!authToken) {
        setShowTokenInput(true)
        setIsLoading(false)
        return
      }
      const response = await fetch(apiUrls.tests.result, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      })
      const contentType = response.headers.get("content-type")
      if (!contentType || !contentType.includes("application/json")) {
        const responseText = await response.text()
        throw new Error(
          `API endpoint returned HTML instead of JSON. This usually means the endpoint doesn't exist or there's a server configuration issue. Response: ${responseText.substring(0, 100)}...`,
        )
      }

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Authentication failed. Please check your token.")
        }
        if (response.status === 404) {
          throw new Error("API endpoint not found. Please check if the server is running and the endpoint exists.")
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      const data = await response.json()
      if (data.results && data.results.length > 0) {
        const mappedResults = data.results.map((result) => {
          // Get the most recent final test (first one in the array due to sorting)
          const latestFinalTest = result.finalTest && result.finalTest.length > 0 
            ? result.finalTest[0] 
            : null;

          if (!latestFinalTest) {
            return null; // Skip results without final tests
          }

          return {
            id: result._id,
            jobId: result._id,
            jobApplicationId: result.jobApplicationId,
            fullName: data.user.fullName,
            email: data.user.email,
            position: result.title,
            location: result.location,
            appliedDate: new Date(result.appliedDate).toLocaleDateString(),
            testDate: new Date(latestFinalTest.createdAt).toLocaleDateString(),
            overallProgress: Math.round(latestFinalTest.scoreProgress),
            aggregateScore: latestFinalTest.totalScore,
            status: latestFinalTest.passed ? "Pass" : "Fail",
            totalQuestions: latestFinalTest.totalQuestions,
            isArchived: latestFinalTest.isArchived,
            // Store all final tests for this application
            allFinalTests: result.finalTest || [],
            modules: [
              {
                title: "Final Test",
                score: latestFinalTest.totalScore,
                status: latestFinalTest.passed ? "completed" : "failed",
                testType: latestFinalTest.testType,
                isPassed: latestFinalTest.passed,
                totalQuestions: latestFinalTest.totalQuestions,
                icon: <FileCheck className="h-6 w-6 text-[#005b9f]" />,
              },
            ],
          };
        }).filter(result => result !== null); // Remove null results

        setResultData(mappedResults)
        setShowTokenInput(false)
      } else {
        setResultData([])
      }
    } catch (error) {

      let errorMessage = "Failed to load test results. Please try again later."

      if (error instanceof Error) {
        if (error.message.includes("<!DOCTYPE")) {
          errorMessage =
            "API endpoint is returning HTML instead of JSON. Please check if the correct API endpoint exists and is configured properly."
        } else {
          errorMessage = error.message
        }
      }

      setError(errorMessage)
      setResultData([])
    } finally {
      setIsLoading(false)
    }
  }

  const handleTokenSubmit = () => {
    if (tempToken.trim()) {
      localStorage.setItem("authToken", tempToken.trim())
      fetchTestResults(tempToken.trim())
    }
  }

  useEffect(() => {
    fetchTestResults()
  }, [])

  const generatePDF = async (result) => {
    try {
      const jsPDFModule = await import("jspdf");
      const jsPDF = jsPDFModule.default;
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;

      const logoUrl =
        "https://res.cloudinary.com/hazer-global/image/upload/v1761115649/uploads/image-1761115635973-5ltg1l.png";

      const getImageBase64 = async (url) => {
        const response = await fetch(url);
        const blob = await response.blob();
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      };

      const logoBase64 = await getImageBase64(logoUrl);
      const logoWidth = 40;
      const logoHeight = 40;
      const logoX = (pageWidth - logoWidth) / 2;
      const logoY = 20;
      doc.addImage(logoBase64, "PNG", logoX, logoY, logoWidth, logoHeight);

      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(31, 41, 55);
      doc.text(`${result.position || "Position"} Certificate`, pageWidth / 2, logoY + 55, { align: "center" });

      let yPos = logoY + 80;
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(55, 65, 81);
      doc.text("Certificate Details", 20, yPos);
      doc.setDrawColor(229, 231, 235);
      doc.line(20, yPos + 5, pageWidth - 20, yPos + 5);
      yPos += 20;

      const leftCol = 20;
      const rightCol = pageWidth / 2 + 10;

      doc.setFontSize(10);
      doc.setTextColor(107, 114, 128);
      doc.setFont("helvetica", "normal");
      doc.text("Candidate Name", leftCol, yPos);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(31, 41, 55);
      doc.text(String(result.fullName || "N/A"), leftCol, yPos + 8);

      doc.setFont("helvetica", "normal");
      doc.setTextColor(107, 114, 128);
      doc.text("Job Title", rightCol, yPos);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(31, 41, 55);
      doc.text(String(result.position || "N/A"), rightCol, yPos + 8);

      doc.setFont("helvetica", "normal");
      doc.setTextColor(107, 114, 128);
      doc.text("Applied Date", leftCol, yPos + 25);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(31, 41, 55);
      doc.text(String(result.appliedDate || "N/A"), leftCol, yPos + 33);

      doc.setFont("helvetica", "normal");
      doc.setTextColor(107, 114, 128);
      doc.text("Test Date", rightCol, yPos + 25);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(31, 41, 55);
      doc.text(String(result.testDate || "N/A"), rightCol, yPos + 33);
      yPos += 55;

      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(55, 65, 81);
      doc.text(`Final Progress: (${result.overallProgress}%)`, 20, yPos);

      yPos += 10;
      const barWidth = pageWidth - 40;
      const barHeight = 6;
      const progressWidth = (barWidth * result.overallProgress) / 100;

      doc.setFillColor(229, 231, 235);
      doc.rect(20, yPos, barWidth, barHeight, "F");
      doc.setFillColor(255, 153, 6);
      doc.rect(20, yPos, progressWidth, barHeight, "F");
      yPos += 25;

      result.modules.forEach((module) => {
        const sectionHeight = 45;
        const sectionWidth = (pageWidth - 60) / 2;
        const leftX = 20;
        const rightX = leftX + sectionWidth + 20;

        doc.setFillColor(234, 246, 255);
        doc.setDrawColor(209, 213, 219);
        doc.roundedRect(leftX, yPos, sectionWidth, sectionHeight, 3, 3, "FD");

        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(17, 24, 39);
        doc.text(module.title, leftX + 5, yPos + 12);

        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(55, 65, 81);
        doc.text(`Score: ${module.score}/${module.totalQuestions}`, leftX + 5, yPos + 22);
        doc.text(`Test Type: ${module.testType}`, leftX + 5, yPos + 30);
        const resultColor = module.isPassed ? [22, 163, 74] : [220, 38, 38];
        doc.setTextColor(...resultColor);
        doc.text(`Result: ${module.isPassed ? "Pass" : "Fail"}`, leftX + 5, yPos + 38);

        if (result.status === "Pass") {
          doc.setFillColor(240, 253, 244);
          doc.setDrawColor(187, 247, 208);
          doc.roundedRect(rightX, yPos, sectionWidth, sectionHeight, 3, 3, "FD");

          doc.setFontSize(11);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(21, 128, 61);
          doc.text("Congratulations", rightX + 5, yPos + 12);

          doc.setFontSize(9);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(22, 101, 52);
          const message =
            "You have passed the final test! Our team will contact you soon for the rest of the process. Best of luck ahead.";
          doc.text(message, rightX + 5, yPos + 22, { maxWidth: sectionWidth - 10 });
        } else if (result.status === "Fail") {
          doc.setFillColor(239, 246, 255);
          doc.setDrawColor(191, 219, 254);
          doc.roundedRect(rightX, yPos, sectionWidth, sectionHeight, 3, 3, "FD");

          doc.setFontSize(11);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(30, 64, 175);
          doc.text("Keep going — you're close!", rightX + 5, yPos + 12);

          doc.setFontSize(9);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(30, 58, 138);
          const message =
            "We regret to inform you that you were not selected this time, but we appreciate your effort and encourage you to reapply.";
          doc.text(message, rightX + 5, yPos + 22, { maxWidth: sectionWidth - 10 });
        }

        yPos += sectionHeight + 15;
      });

      // Add attempt history if there are multiple attempts
      if (result.allFinalTests && result.allFinalTests.length > 1) {
        yPos += 10;
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(55, 65, 81);
        doc.text("Attempt History", 20, yPos);
        yPos += 10;

        result.allFinalTests.forEach((attempt, index) => {
          if (yPos > 250) {
            doc.addPage();
            yPos = 20;
          }
          
          doc.setFontSize(9);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(107, 114, 128);
          doc.text(`Attempt ${index + 1}: ${new Date(attempt.createdAt).toLocaleDateString()}`, 20, yPos);
          
          doc.setFont("helvetica", "bold");
          doc.setTextColor(31, 41, 55);
          doc.text(`Score: ${attempt.totalScore}/${attempt.totalQuestions} (${attempt.scoreProgress}%)`, 20, yPos + 6);
          
          const attemptStatus = attempt.passed ? "Pass" : "Fail";
          const statusColor = attempt.passed ? [22, 163, 74] : [220, 38, 38];
          doc.setTextColor(...statusColor);
          doc.text(`Status: ${attemptStatus}`, 20, yPos + 12);
          
          yPos += 20;
        });
      }

      return doc;
    } catch (error) {
      console.error("Error generating PDF:", error);
      throw error;
    }
  };

  const handleDownload = async (result) => {
    try {
      const doc = await generatePDF(result)
      doc.save(`${result.fullName}_${result.position}_Certificate.pdf`)
    } catch (error) {
      console.error("Error downloading PDF:", error)
      Swal.fire({
        title: 'Error!',
        text: 'Error generating PDF. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'rounded-lg shadow-xl',
          confirmButton: 'bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded'
        }
      })
    }
  }

  const handleShare = async (result) => {
    try {
      const doc = await generatePDF(result)
      const pdfBlob = doc.output("blob")

      if (
        navigator.share &&
        navigator.canShare({ files: [new File([pdfBlob], "certificate.pdf", { type: "application/pdf" })] })
      ) {
        const file = new File([pdfBlob], `${result.fullName}_Certificate.pdf`, { type: "application/pdf" })
        await navigator.share({
          title: "Certificate",
          text: `${result.fullName}'s ${result.position} Certificate`,
          files: [file],
        })
      } else {
        await handleDownload(result)
      }
    } catch (error) {
      console.error("Error sharing PDF:", error)
      Swal.fire({
        title: 'Error!',
        text: 'Error generating PDF. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'rounded-lg shadow-xl',
          confirmButton: 'bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded'
        }
      })
    }
  }

  const handleReapply = async (application) => {
    if (!application.jobApplicationId) {
      Swal.fire({
        title: 'Error!',
        text: 'Unable to reapply. Job Application ID not found.',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'rounded-lg shadow-xl',
          confirmButton: 'bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded'
        }
      })
      return
    }

    const confirmationResult = await Swal.fire({
      title: 'Reapply for Position?',
      text: "Are you sure you want to reapply for this position? This will archive your current application.",
      icon: 'question',
      width: 600,
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, reapply!',
      cancelButtonText: 'Cancel',
      customClass: {
        popup: 'rounded-lg shadow-xl',
        confirmButton: 'bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded',
        cancelButton: 'bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded'
      }
    })

    if (!confirmationResult.isConfirmed) return

    try {
      setIsReapplying(true)
      const authToken = localStorage.getItem("authToken")

      // Use jobApplicationId instead of jobId for archiving
      const response = await fetch(`${apiUrls.appstatus.archiveApp}/${application.jobApplicationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to reapply: ${response.status}`)
      }

      const data = await response.json()
      
      await Swal.fire({
        title: 'Success!',
        text: 'Application archived successfully! You can now reapply for this position.',
        icon: 'success',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'rounded-lg shadow-xl',
          confirmButton: 'bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded'
        }
      })

      navigate("/dashboard", { replace: true });
      window.location.reload();
      
      
      await fetchTestResults()
    } catch (error) {
      console.error("Error reapplying:", error)
      Swal.fire({
        title: 'Error!',
        text: error.message || 'Failed to process reapply request. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'rounded-lg shadow-xl',
          confirmButton: 'bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded'
        }
      })
    } finally {
      setIsReapplying(false)
    }
  }

  const openModal = (result) => {
    setSelectedResult(result)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setSelectedResult(null)
  }

  if (showTokenInput) {
    return (
      <div className="flex justify-center items-center h-screen bg-white/50 dark:bg-black/40">
        <div className="bg-white shadow-2xl rounded-3xl p-8 max-w-lg text-center dark:bg-slate-800 dark:shadow-none">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3 dark:text-gray-100">Authentication Required</h2>
          <p className="text-gray-600 mb-6 dark:text-gray-300">Please enter your authentication token to access the test results.</p>

          <div className="space-y-4">
            <input
              type="text"
              placeholder="Enter your auth token"
              value={tempToken}
              onChange={(e) => setTempToken(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-700 dark:border-slate-600 dark:text-gray-100"
            />
            <Button
              onClick={handleTokenSubmit}
              className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
              disabled={!tempToken.trim()}
            >
              Access Results
            </Button>
          </div>

          <div className="mt-6 text-sm text-gray-500 dark:text-gray-400">
            <p>Don't have a token? Contact your administrator.</p>
          </div>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <Loader/>
    )
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen bg-white/50 dark:bg-black/40">
        <div className="bg-white shadow-2xl rounded-3xl p-8 max-w-lg text-center dark:bg-slate-800 dark:shadow-none">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center dark:bg-red-900">
              <X className="w-8 h-8 text-red-600 dark:text-red-300" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3 dark:text-gray-100">Error Loading Results</h2>
          <p className="text-gray-600 mb-4 dark:text-gray-300">{error}</p>
          <div className="space-y-2">
            <Button onClick={() => fetchTestResults()} className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 mr-2">
              Try Again
            </Button>
            <Button onClick={() => setShowTokenInput(true)} variant="outline">
              Update Token
            </Button>
          </div>
        </div>
      </div>
    )
  }

  if (!resultData || resultData.length === 0) {
    return (
      <div className="flex justify-center items-start h-screen bg-white/50 dark:bg-black/40">
        <div className="bg-white shadow-2xl rounded-3xl p-8 max-w-lg text-center transform transition-all duration-300 hover:scale-105 dark:bg-slate-800">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center animate-bounce dark:bg-indigo-900">
              <svg
                className="w-8 h-8 text-indigo-600 dark:text-indigo-200"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3 dark:text-gray-100">No Results Found</h2>
          <p className="text-gray-600 leading-relaxed dark:text-gray-300">
            We couldn't find any test results at the moment. Please check back later or contact{" "}
            <span className="font-semibold text-red-600 dark:text-red-400">support</span>.
          </p>
          <div className="mt-6 text-sm text-gray-400 dark:text-gray-500">Thank you for your understanding 🙏</div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="container mx-auto p-6">
        <Card className="bg-white dark:bg-slate-800">
          <CardHeader>
            <CardTitle className="text-gray-900 dark:text-gray-50">Test Results</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 dark:bg-slate-700">
                  <TableHead className="text-gray-800 dark:text-gray-100">Candidate</TableHead>
                  <TableHead className="text-gray-800 dark:text-gray-100">Job title</TableHead>
                  <TableHead className="text-gray-800 dark:text-gray-100">Location</TableHead>
                  <TableHead className="text-gray-800 dark:text-gray-100">Applied Date</TableHead>
                  <TableHead className="text-gray-800 dark:text-gray-100">Test Date</TableHead>
                  <TableHead className="text-gray-800 dark:text-gray-100">Score</TableHead>
                  <TableHead className="text-gray-800 dark:text-gray-100">Status</TableHead>
                  <TableHead className="text-gray-800 dark:text-gray-100">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {resultData?.map((result) => (
                  <TableRow key={result?.jobApplicationId} className="hover:bg-gray-50 dark:hover:bg-slate-700">
                    <TableCell className="font-medium text-gray-800 dark:text-gray-100">{result?.fullName || "N/A"}</TableCell>
                    <TableCell className="text-gray-700 dark:text-gray-300">{result?.position || "N/A"}</TableCell>
                    <TableCell className="text-gray-700 dark:text-gray-300">{result?.location || "N/A"}</TableCell>
                    <TableCell className="text-gray-700 dark:text-gray-300">{result?.appliedDate}</TableCell>
                    <TableCell className="text-gray-700 dark:text-gray-300">{result?.testDate}</TableCell>
                    <TableCell className="text-gray-700 dark:text-gray-300">
                      {result?.aggregateScore}/{result?.totalQuestions}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-2 items-center">
                        <span
                          className={`px-6 py-1 rounded-full text-xs font-semibold w-fit ${result.status === "Pass"
                              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                              : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"
                            }`}
                        >
                          {result.status}
                        </span>
                        {result.status === "Fail" && !result.isArchived && (
                          <button
                            onClick={() => handleReapply(result)}
                            disabled={isReapplying}
                            className={`px-1 py-1 rounded-full font-bold text-sm transition-colors duration-200 w-fit ${
                              isReapplying
                                ? "bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-600 dark:text-gray-400"
                                : "bg-blue-100 text-blue-800 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-200 dark:hover:bg-blue-800"
                            }`}
                          >
                            {isReapplying ? "Processing..." : "Re-apply"}
                          </button>
                        )}
                        {result.isArchived && (
                          <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600 dark:bg-gray-600 dark:text-gray-300">
                            Archived
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openModal(result)}
                          className="h-8 w-8 p-0 hover:bg-blue-100 dark:hover:bg-blue-900"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4 text-blue-600 dark:text-blue-200" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownload(result)}
                          className="h-8 w-8 p-0 hover:bg-green-100 dark:hover:bg-green-900"
                          title="Download PDF"
                        >
                          <Download className="h-4 w-4 text-green-600 dark:text-green-200" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleShare(result)}
                          className="h-8 w-8 p-0 hover:bg-yellow-100 dark:hover:bg-yellow-800"
                          title="Share Certificate"
                        >
                          <Share2 className="h-4 w-4 text-yellow-600 dark:text-yellow-200" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {isModalOpen && selectedResult && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <Card className="w-[900px] max-h-[90vh] bg-white shadow-xl rounded-lg relative border border-gray-300 p-6 overflow-y-auto">
            <button
              onClick={closeModal}
              className="absolute top-2 right-4 text-gray-700 p-2 rounded-full hover:bg-[#ff9906] transition"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="text-center mt-[-40px]">
              <div className="mx-auto w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                <img
                  src={Logo || "/placeholder.svg"}
                  alt="logo"
                  className="h-20 w-auto transition-opacity duration-300 opacity-100"
                />
              </div>
            </div>

            <CardHeader className="text-center">
              <CardTitle className="text-xl font-bold text-gray-800">{selectedResult.position} Certificate</CardTitle>
            </CardHeader>

            <CardContent className="px-6 space-y-6">
              <div className="border-b pb-3">
                <h3 className="text-lg font-semibold text-gray-700">Certificate Details</h3>
                <div className="grid grid-cols-2 gap-y-4 text-sm text-gray-600 mt-2">
                  <div>
                    <p className="text-gray-500 font-medium">Candidate Name</p>
                    <p className="mt-1 font-semibold">{selectedResult?.fullName}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 font-medium">Job Title</p>
                    <p className="mt-1 font-semibold">{selectedResult?.position}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 font-medium">Applied Date</p>
                    <p className="mt-1 font-semibold">{selectedResult?.appliedDate}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 font-medium">Test Date</p>
                    <p className="mt-1 font-semibold">{selectedResult?.testDate}</p>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-gray-700 font-semibold">
                  Final Progress: ({selectedResult.overallProgress}%)
                </p>
                <Progress value={selectedResult.overallProgress} className="h-3 bg-gray-200" />
              </div>

              <div className="flex flex-col md:flex-row gap-6 justify-between mt-6">
                {selectedResult?.modules?.map((module, index) => (
                  <div key={index} className="flex flex-col md:flex-row w-full gap-6">

                    <div className="md:w-2/5 w-full bg-gradient-to-br from-[#eaf6ff] to-[#f5fbff] p-6 rounded-xl shadow-md border border-gray-200">
                      <div className="flex items-center gap-3 mb-4">
                        {module.icon}
                        <h2 className="font-semibold text-lg text-[#003b73]">{module.title}</h2>
                      </div>

                      <div className="space-y-3 text-gray-800 text-sm">
                        <p className="flex items-center gap-2 font-medium">
                          <CheckCircle className="h-4 w-4 text-[#005b9f]" />
                          Final Score: <span className="font-semibold text-gray-900">{module.score}/{module.totalQuestions}</span>
                        </p>
                        <p className="flex items-center gap-2 font-medium">
                          <Calendar className="h-4 w-4 text-[#005b9f]" />
                          Test Type: <span className="font-semibold text-gray-900">{module.testType}</span>
                        </p>
                        <p className="flex items-center gap-2 font-medium">
                          <Calendar className="h-4 w-4 text-[#005b9f]" />
                          Result:
                          <span
                            className={`font-semibold ${module.isPassed ? "text-green-600" : "text-red-600"
                              }`}
                          >
                            {module.isPassed ? "Pass" : "Fail"}
                          </span>
                        </p>
                      </div>
                    </div>

                    <div className="flex-1">
                      {selectedResult.status === "Fail" ? (
                        <div className="p-4 rounded-lg border bg-red-50 border-red-200">
                          <div className="flex gap-3">
                            <svg
                              className="h-5 w-5 text-red-500 mt-1"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                              aria-hidden="true"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-11.5a.75.75 0 10-1.5 0v4.25a.75.75 0 001.5 0V6.5zM10 14a1 1 0 100 2 1 1 0 000-2z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <div>
                              <h3 className="text-sm font-bold text-red-900">
                                Keep going — you're close!
                              </h3>
                              <p className="mt-1 text-sm text-red-800 leading-relaxed">
                                We regret to inform you that you were not selected this time,
                                but we appreciate your effort and encourage you to reapply for
                                the same or new roles in the future.
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : selectedResult.status === "Pass" ? (
                        <div className="p-4 rounded-lg border bg-green-50 border-green-200">
                          <div className="flex gap-3">
                            <svg
                              className="h-5 w-5 text-green-500 mt-1"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                              aria-hidden="true"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.704 5.29a1 1 0 00-1.408-1.42L8 11.17 4.704 7.88a1 1 0 00-1.408 1.42l4 4a1 1 0 001.408 0l8-8z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <div>
                              <h3 className="text-sm font-bold text-green-900">
                                Congratulations 🎉
                              </h3>
                              <p className="mt-1 text-sm text-green-800 leading-relaxed">
                                You have passed the final test! Our team will contact you soon
                                for the rest of the process. Best of luck ahead.
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>

              {/* Attempt History Section */}
              {selectedResult.allFinalTests && selectedResult.allFinalTests.length > 1 && (
                <div className="mt-6 border-t pt-4">
                  <h3 className="text-lg font-semibold text-gray-700 mb-3">Attempt History</h3>
                  <div className="bg-gray-50 rounded-lg p-4 dark:bg-slate-700">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Attempt Date</TableHead>
                          <TableHead>Score</TableHead>
                          <TableHead>Progress</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedResult.allFinalTests.map((attempt, index) => (
                          <TableRow key={attempt._id}>
                            <TableCell className="text-sm">
                              {new Date(attempt.createdAt).toLocaleDateString()}
                            </TableCell>
                            <TableCell className="text-sm">
                              {attempt.totalScore}/{attempt.totalQuestions}
                            </TableCell>
                            <TableCell className="text-sm">
                              {attempt.scoreProgress}%
                            </TableCell>
                            <TableCell>
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                  attempt.passed
                                    ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                    : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"
                                }`}
                              >
                                {attempt.passed ? "Pass" : "Fail"}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </>
  )
}